import { useEffect } from "react";

import { Input } from "common/components/ui/Input";
import Label from "common/components/ui/Label";
import Skeleton from "common/components/ui/Skeleton";
import Slider from "common/components/ui/Slider";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

const STEP = 10;
const MIN_VALUE = 10;

export default function LimitsEditItem({
  label,
  value,
  setValue,
  maxValue,
  isLoading,
  color,
}: {
  label: string;
  value: number;
  setValue: (value: number) => void;
  maxValue: number;
  isLoading: boolean;
  color: "orange" | "purple";
}) {
  const isDesktop = useTwBreakpoint("lg");

  useEffect(() => {
    if (value > maxValue) {
      setValue(maxValue);
    }
  }, [maxValue]);

  return (
    <div className="flex flex-col gap-3 rounded-3xl border-black-200 p-2 border has-[input:focus-visible]:bg-black-50 transition-[background]">
      <div className="flex justify-between items-center">
        <Label size="sm" className="text-black-500" htmlFor={label}>
          {label}
        </Label>

        {isLoading ? (
          <Skeleton className="h-8 w-10 shrink-0" />
        ) : (
          <Input
            variant="sm"
            id={label}
            className="w-10 text-center"
            value={value}
            type="text"
            onChange={({ target: { value: inputValue } }) => {
              const numericValue = Number(inputValue);

              if (!Number.isNaN(numericValue)) {
                setValue(numericValue);
              }
            }}
            onBlur={({ target: { value: inputValue } }) => {
              const numericValue = Number(inputValue);

              if (
                !Number.isNaN(numericValue) &&
                numericValue >= MIN_VALUE &&
                numericValue <= maxValue
              ) {
                setValue(numericValue);
              } else {
                // Determine which value to set based on proximity
                const isCloserToMax =
                  Math.abs(numericValue - maxValue) <
                  Math.abs(numericValue - MIN_VALUE);
                setValue(isCloserToMax ? maxValue : MIN_VALUE);
              }
            }}
          />
        )}
      </div>

      <div className="flex flex-col gap-y-3 lg:gap-y-2 px-1">
        {isLoading ? (
          <Skeleton className="h-1 w-full" />
        ) : (
          <Slider
            className="w-full"
            size={isDesktop ? "sm" : "lg"}
            value={[value]}
            onValueChange={(val) => {
              const newValue = Math.max(val[0], MIN_VALUE);
              setValue(newValue);
            }}
            max={maxValue}
            min={MIN_VALUE}
            step={STEP}
            variant={color}
          />
        )}

        <div className="flex justify-between">
          <div className="flex flex-col">
            <span className="text-button-xs">{MIN_VALUE}</span>
            <span className="text-black-500 text-button-xs">Min</span>
          </div>

          <div className="flex flex-col items-end">
            <span className="text-button-xs">{maxValue}</span>
            <span className="text-black-500 text-button-xs">Max</span>
          </div>
        </div>
      </div>
    </div>
  );
}
